import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { IMessage } from '../../redux/messages/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { ILocation } from '../../types/global/helper';
import { MessageActions } from './MessageActions';

interface LocationMessageComponentProps {
    message: IMessage;
    isCurrentUser: boolean;
    onReactionClick?: (emoji: string) => void;
    onDeleteMessage?: (messageId: string) => void;
    formatMessageTime?: (timestamp?: string) => string;
}

export const LocationMessageComponent: React.FC<LocationMessageComponentProps> = ({
    message,
    isCurrentUser,
    onReactionClick = () => {},
    onDeleteMessage,
    formatMessageTime = () => '',
}) => {
    const { themeStyle } = useThemeContext();

    // Extract location data
    const location = message.location;
    const { address, coordinates, description, short_name } = location as ILocation;

    const lng = coordinates[1];
    const lat = coordinates[0];

    // Generate Google Maps URL
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

    const handleOpenLocation = () => {
        window.open(mapsUrl, '_blank');
    };

    return (
        <Box
            sx={{
                width: { xs: '100%', sm: '280px' },
                borderRadius: '16px',
                overflow: 'hidden',
                backgroundColor: theme[themeStyle].deep_grey,
                border: `1px solid ${theme[themeStyle].grey}30`,
            }}>
            {/* Map Preview */}
            <Box
                sx={{
                    width: '100%',
                    height: '150px',
                    position: 'relative',
                    backgroundColor: theme[themeStyle].deep_grey_lighter,
                }}>
                {/* Static Map Image */}
                <Box
                    component="img"
                    src={`https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=14&size=400x200&markers=color:red%7C${lat},${lng}&key=AIzaSyDRHSBEt5bK6rWO0eD3OslkhlKQg0-W4zA`}
                    alt="Map location"
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />

                {/* Location Pin Icon (centered) */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                    }}>
                    <LocationOnIcon sx={{ color: theme[themeStyle].highlight, fontSize: 40 }} />
                </Box>
            </Box>

            {/* Location Details */}
            <Box sx={{ p: 2 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '16px',
                        color: theme[themeStyle].tag_white,
                        mb: 1,
                    }}>
                    {short_name ?? address ?? description}
                </Typography>

                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        fontSize: '12px',
                        color: theme[themeStyle].grey,
                        mb: 2,
                    }}>
                    Latitude: {lat.toFixed(6)}, Longitude: {lng.toFixed(6)}
                </Typography>

                <Button
                    variant="contained"
                    onClick={handleOpenLocation}
                    fullWidth
                    sx={{
                        backgroundColor: theme[themeStyle].highlight,
                        color: theme[themeStyle].true_white,
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: theme[themeStyle].highlight,
                        },
                    }}>
                    Open in Maps
                </Button>

                {/* Message info footer */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: isCurrentUser ? 'rgba(255, 255, 255, 0.7)' : theme[themeStyle].grey,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        {formatMessageTime(message.createdAt)}
                    </Typography>

                    {/* Add message actions */}
                    <MessageActions
                        message={message}
                        isCurrentUser={isCurrentUser}
                        onReactionClick={onReactionClick}
                        onDeleteMessage={onDeleteMessage}
                    />
                </Box>
            </Box>
        </Box>
    );
};
